import { ContentfulPageProps } from "../types/vino-data-types";
import { Layout } from "../components/layout";
import { PageHeader } from "../components/page-header";
import PageSection from "../component-factory/page-section";
import { useMemo, useState, useEffect } from "react";
import SEO from "../components/seo/seo";
import SearchProvider from "../contexts/search/provider";
import { getPageSeoTitle } from "../utils/get-seo-title";
import useAuth from "../hooks/use-auth";
import { ProductPreloader } from "../components/preloaders/ProductPreloader";
import config from "../../config.json";
import { VinoUser } from "../contexts/auth/state";
import { determineTierIndex, isUserAStaff } from "../utils/loyalty";
import PayWall from "../components/pay-wall/PayWall";

const ContentfulPage: React.FC<ContentfulPageProps> = ({
  pageContext,
}): JSX.Element => {
  const { user, isLoading: authLoading } = useAuth();
  const { id, page, pagePath } = pageContext;
  const {
    title,
    pageSections,
    noIndex,
    seoTitle,
    seoDescription,
    route,
    heroImage,
    internalName,
    tiersEligible,
  } = page;
  
  const [isEligible, setIsEligible] = useState(
    !tiersEligible || tiersEligible?.includes("ALL")
  );
  const [modal, setModal] = useState<any>(<ProductPreloader />);
  const { tier } = determineTierIndex(
    user as VinoUser,
    true,
    user?.customer_group?.name
  );
  const tierMap = config[process.env.GATSBY_VM_MARKET]["TIER_MAP"] || {};
  const isCustomerEligible = useMemo(
    () =>
      Array.isArray(tiersEligible) &&
      tiersEligible.some(
        (pageTier) =>
          tierMap[tier?.toUpperCase() || "GREEN"] >=
          tierMap[pageTier.toUpperCase()]
      ),
    [tiersEligible, tier]
  );

  useEffect(() => {
    if (!tiersEligible || tiersEligible?.includes("ALL")) {
      setIsEligible(true);
      return;
    }
    if (authLoading) {
      return;
    }
    if (user && isUserAStaff(user)) {
      setIsEligible(true);
      return;
    }
    if (!user && !tiersEligible?.includes("GUEST")) {
      setModal(
        <PayWall
          title={"Login to see all of your exclusive VIM deals."}
          LoginButton={true}
          cause={"guest_cannot_see"}
          theme="modal"
          category="Spirit"
          page_tier={tiersEligible.toString()}
          user_tier={tier?.toUpperCase()}
        />
      );
      setIsEligible(false);
      return;
    }
    if (!user && tiersEligible?.includes("GUEST")) {
      setIsEligible(true);
      return;
    }
    if (!tier) {
      return;
    }
    if (!isCustomerEligible) {
      setModal(
        <PayWall
          title={
            "This is awkward... You'll need to make a purchase to unlock exclusive deals. Shop now to see what you're missing!"
          }
          LoginButton={false}
          cause={"tier_too_low"}
          theme="modal"
          category="Spirit"
          page_tier={tiersEligible.toString()}
          user_tier={user?.customer_group?.name?.toUpperCase()}
        />
      );
      setIsEligible(false);
      return;
    }
    setIsEligible(true);
  }, [user, authLoading, isCustomerEligible]);

  if (!isEligible) {
    return (
      <Layout
        pathname={pagePath}
        bgImage={page?.backgroundImage?.url}
        isWhiskymofo={route?.path?.includes("whiskymofo")}
        footerLayout={page?.footerLayout}
      >
        <SEO
          title={seoTitle}
          type={"event"}
          image={heroImage?.url || page?.backgroundImage?.url || ""}
          pathname={route?.path || ""}
          description={seoDescription || ""}
          robots={noIndex ? "noindex, nofollow" : "index, follow"}
        />
        {modal}
      </Layout>
    );
  }

  return (
    <Layout
      pathname={pagePath}
      bgImage={page?.backgroundImage?.url}
      isWhiskymofo={route?.path?.includes("whiskymofo")}
      footerLayout={page?.footerLayout}
    >
      <SEO
        title={getPageSeoTitle(title, seoTitle, internalName)}
        description={seoDescription}
        type={"page"}
        pathname={pagePath}
        robots={noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <SearchProvider>
        <PageHeader {...page} pagePath={pagePath} />
        {pageSections.map((section, index) => {
          return PageSection.create({ ...section, pageId: id }, index);
        })}
      </SearchProvider>
    </Layout>
  );
};

export default ContentfulPage;
